import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DayritLogo } from '../components/ui';
import { offlineStorage, habitOfflineStorage } from '../utils/offlineStorage';
import { authApi } from '../api';

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 0.7s linear infinite', flexShrink: 0 }}>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  );
}

function FieldInput({ label, type = 'text', placeholder, value, onChange, error, autoFocus, hint, rightEl }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          type={type} placeholder={placeholder} value={value} onChange={onChange} autoFocus={autoFocus}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: rightEl ? '11px 42px 11px 14px' : '11px 14px',
            fontSize: 14, fontFamily: 'inherit',
            border: `1.5px solid ${error ? '#EF4444' : focused ? 'var(--accent)' : 'var(--border)'}`,
            borderRadius: 10, background: 'var(--input-bg)', color: 'var(--text)', outline: 'none',
            boxShadow: focused && !error ? '0 0 0 3px var(--accent-ring)' : 'none', transition: 'all 0.15s',
          }}
        />
        {rightEl && <div style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)' }}>{rightEl}</div>}
      </div>
      {error && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 4, fontWeight: 500 }}>{error}</p>}
      {hint && !error && <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4 }}>{hint}</p>}
    </div>
  );
}

function PasswordInput({ label, placeholder, value, onChange, error, autoFocus }) {
  const [show, setShow] = useState(false);
  return (
    <FieldInput
      label={label} type={show ? 'text' : 'password'}
      placeholder={placeholder} value={value} onChange={onChange}
      error={error} autoFocus={autoFocus}
      rightEl={
        <button type="button" onClick={() => setShow(p => !p)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', padding: 0, display: 'flex', lineHeight: 1 }}>
          {show
            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          }
        </button>
      }
    />
  );
}

function buildOfflineLabel(taskCount, habitCount) {
  const parts = [];
  if (taskCount > 0) parts.push(`${taskCount} task${taskCount !== 1 ? 's' : ''}`);
  if (habitCount > 0) parts.push(`${habitCount} habit${habitCount !== 1 ? 's' : ''}`);
  return parts.join(' & ');
}

export default function LoginPage() {
  const { login, syncing, syncDone } = useAuth();
  const navigate = useNavigate();

  const [form,    setForm]    = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const offlineTaskCount  = offlineStorage.count();
  const offlineHabitCount = habitOfflineStorage.count();
  const offlineCount      = offlineTaskCount + offlineHabitCount;
  const offlineLabel      = buildOfflineLabel(offlineTaskCount, offlineHabitCount);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      setError('Please enter your email and password.');
      return;
    }
    setLoading(true); setError('');
    try {
      const res = await authApi.login({ email: form.email, password: form.password });
      const { token } = res.data;
      localStorage.setItem('dayrit_token', token);
      // Reload so AuthContext re-boots with the stored token
      window.location.replace('/app');
    } catch (err) {
      const data = err.response?.data;

      // Email not verified → redirect to verify page
      if (err.response?.status === 403 && data?.email_unverified) {
        localStorage.setItem('dayrit_pending_email', data.email || form.email);
        navigate('/verify-email', {
          replace: true,
          state: { email: data.email || form.email },
        });
        return;
      }

      setError(
        data?.errors?.email?.[0] ||
        data?.errors?.password?.[0] ||
        data?.message ||
        'Invalid email or password.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif" }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* ── Left / main form ── */}
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'clamp(24px,5vw,48px) clamp(16px,5vw,48px)', minWidth: 0 }}>
        <div style={{ width: '100%', maxWidth: 380 }}>

          <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 9, textDecoration: 'none', marginBottom: 36 }}>
            <DayritLogo size={32} />
            <span style={{ fontSize: 18, fontWeight: 800, background: 'var(--brand-grad)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Dayrit</span>
          </Link>

          <h1 style={{ fontSize: 26, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.6px', marginBottom: 6 }}>Welcome back</h1>
          <p style={{ fontSize: 14, color: 'var(--text3)', marginBottom: 28, lineHeight: 1.5 }}>Sign in to your account to continue.</p>

          {/* Offline sync banner */}
          {offlineCount > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', borderRadius: 12, background: 'var(--accent-light)', border: '1px solid var(--accent-ring)', marginBottom: 20 }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>☁️</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)' }}>{offlineLabel} ready to sync</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>Sign in and they'll upload automatically</div>
              </div>
            </div>
          )}

          {syncing && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderRadius: 10, background: 'var(--surface)', border: '1px solid var(--border)', marginBottom: 16 }}>
              <Spinner /><span style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500 }}>Syncing offline tasks…</span>
            </div>
          )}
          {syncDone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px', borderRadius: 10, background: 'rgba(22,163,74,0.08)', border: '1px solid rgba(22,163,74,0.25)', marginBottom: 16, fontSize: 13, color: '#16A34A', fontWeight: 600 }}>
              ✓ All offline tasks synced!
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <FieldInput label="Email" type="email" placeholder="you@example.com"
              value={form.email} onChange={e => set('email', e.target.value)} autoFocus />

            <div>
              <PasswordInput label="Password" placeholder="Your password"
                value={form.password} onChange={e => set('password', e.target.value)} />
              <div style={{ textAlign: 'right', marginTop: 6 }}>
                <Link to="/forgot-password" style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>Forgot password?</Link>
              </div>
            </div>

            {error && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 13px', borderRadius: 9, background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', fontSize: 13, color: '#DC2626', fontWeight: 500 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                {error}
              </div>
            )}

            <button type="submit" disabled={loading} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              padding: '13px 20px', borderRadius: 11, border: 'none',
              background: loading ? 'var(--border)' : 'var(--brand-grad)',
              color: loading ? 'var(--text3)' : 'white',
              fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit', transition: 'all 0.15s', minHeight: 48,
              boxShadow: loading ? 'none' : '0 4px 16px var(--accent-ring)',
            }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.filter = 'brightness(1.08)'; }}
              onMouseLeave={e => e.currentTarget.style.filter = ''}
            >
              {loading ? <><Spinner />Signing in…</> : 'Sign in →'}
            </button>
          </form>

          {/* Guest CTA */}
          <div style={{ marginTop: 18, padding: '14px 16px', borderRadius: 11, background: 'var(--surface)', border: '1px solid var(--border)', textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 10 }}>Just want to try it out?</p>
            <Link to="/app" style={{
              display: 'block', padding: '10px', borderRadius: 8,
              border: '1.5px solid var(--border)', fontSize: 13, fontWeight: 600,
              color: 'var(--text2)', textDecoration: 'none', transition: 'all 0.15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text2)'; }}
            >
              Continue without account →
            </Link>
          </div>

          <p style={{ textAlign: 'center', marginTop: 18, fontSize: 13, color: 'var(--text3)' }}>
            No account yet?{' '}
            <Link to="/register" style={{ color: 'var(--accent)', fontWeight: 700, textDecoration: 'none' }}>Sign up free</Link>
          </p>
        </div>
      </div>

      {/* ── Right panel — hidden on mobile ── */}
      <div style={{ width: 420, background: 'var(--accent)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 48, position: 'relative', overflow: 'hidden' }} className="auth-right-panel">
        <style>{`
          @media(max-width:767px){ .auth-right-panel{ display:none!important; } }
        `}</style>
        <div style={{ position: 'absolute', top: -60, right: -60, width: 240, height: 240, borderRadius: '50%', background: 'rgba(255,255,255,0.1)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -40, left: -40, width: 180, height: 180, borderRadius: '50%', background: 'rgba(255,255,255,0.07)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', maxWidth: 300, zIndex: 1 }}>
          <div style={{ fontSize: 52, marginBottom: 24 }}>✅</div>
          <h2 style={{ fontSize: 26, fontWeight: 800, color: 'white', letterSpacing: '-0.5px', lineHeight: 1.2, marginBottom: 12 }}>Your tasks, always with you</h2>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.75)', lineHeight: 1.65, marginBottom: 28 }}>
            Works offline, syncs when you connect. Never lose a task again.
          </p>
          {['Works 100% offline', 'Syncs automatically', 'Subtasks, habits & Pomodoro'].map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="11" height="9" viewBox="0 0 11 9" fill="none"><path d="M1 4.5L4 7.5L10 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </div>
              <span style={{ fontSize: 14, color: 'rgba(255,255,255,0.9)', fontWeight: 500 }}>{f}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}